import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.less';

export interface AutoScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  deps?: ReadonlyArray<unknown>;
  forceScrollDeps?: ReadonlyArray<unknown>;
  bottomThreshold?: number;
}

const AutoScrollContainer: React.FC<AutoScrollContainerProps> = ({
  children,
  className,
  deps = [],
  forceScrollDeps = [],
  bottomThreshold = 24,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  const mergedClassName = useMemo(
    () => (className ? `auto-scroll-container ${className}` : 'auto-scroll-container'),
    [className],
  );

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const isAtBottom = (el: HTMLDivElement) =>
    el.scrollHeight - (el.scrollTop + el.clientHeight) <= bottomThreshold;

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const atBottom = isAtBottom(el);
    setAutoScrollEnabled((prev) => {
      if (atBottom && !prev) return true;
      if (!atBottom && prev) return false;
      return prev;
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, []);

  useEffect(() => {
    if (!autoScrollEnabled) return;
    scrollToBottom();
  }, [autoScrollEnabled, ...deps]);

  useEffect(() => {
    scrollToBottom();
    setAutoScrollEnabled(true);
  }, [...forceScrollDeps]);

  return (
    <div ref={containerRef} className={mergedClassName} onScroll={handleScroll}>
      {children}
    </div>
  );
};

export default AutoScrollContainer;
